import React, { useState, useEffect, useContext } from 'react';
import { Button, Input, Label, FormGroup } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSave } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { PostData } from '../../../services/FetchData';
import { ProductContext } from '../../../store';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';

const QuestionForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { currentWebsiteId } = useContext(ProductContext);
    const isEdit = location.pathname.includes('/edit');
    const [questionId, setQuestionId] = useState(null);
    const [isPythonMode, setIsPythonMode] = useState(false);
    const [productId, setProductId] = useState('');
    const [pythonQuestions, setPythonQuestions] = useState(['', '', '']);
    const [formData, setFormData] = useState({
        question: '',
        answer: '',
        sku: '',
        isActive: true
    });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const savedProduct = localStorage.getItem('editingProductQuestions');

        if (isEdit && savedProduct) {
            try {
                const product = JSON.parse(savedProduct);
                if (product.isPythonRagTraining) {
                    setIsPythonMode(true);
                    setProductId(product.productId || '');
                    setFormData(prev => ({
                        ...prev,
                        sku: product.sku || '',
                    }));
                    const questions = product.questions || ['', '', ''];
                    setPythonQuestions([
                        questions[0] || '',
                        questions[1] || '',
                        questions[2] || '',
                    ]);
                    setErrors({});
                    return;
                }
            } catch (error) {
                console.error("Error parsing saved product questions:", error);
            }
        }

        setIsPythonMode(false);

        if (isEdit) {
            const savedQuestion = localStorage.getItem('editingQuestion');
            if (savedQuestion) {
                try {
                    const question = JSON.parse(savedQuestion);

                    if (!question.id) {
                        toast.error("Question ID is missing from saved data. Please try again.");
                        navigate('/aiassistant/questions');
                        return;
                    }

                    setQuestionId(String(question.id));
                    setFormData({
                        question: question.question || '',
                        answer: question.answer || '',
                        sku: question.sku || '',
                        isActive: question.isActive !== undefined ? question.isActive : true
                    });
                } catch (error) {
                    console.error("Error parsing saved question:", error);
                    toast.error("Error loading question data. Please try again.");
                    navigate('/aiassistant/questions');
                }
            } else {
                toast.error("No question data found. Please select a question to edit.");
                navigate('/aiassistant/questions');
            }
        } else {
            setQuestionId(null);
            setFormData({
                question: '',
                answer: '',
                sku: '',
                isActive: true
            });
            setPythonQuestions(['', '', '']);
        }
        setErrors({});
    }, [isEdit, navigate]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handlePythonQuestionChange = (index, value) => {
        setPythonQuestions(prev => {
            const next = [...prev];
            next[index] = value;
            return next;
        });
        const errorKey = `question${index + 1}`;
        if (errors[errorKey]) {
            setErrors(prev => ({
                ...prev,
                [errorKey]: ''
            }));
        }
    };

    const validateLegacyForm = () => {
        const newErrors = {};

        if (!formData.question.trim()) {
            newErrors.question = 'Question is required';
        }

        if (!formData.sku.trim()) {
            newErrors.sku = 'Product SKU is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validatePythonForm = () => {
        const newErrors = {};

        pythonQuestions.forEach((q, index) => {
            if (!q.trim()) {
                newErrors[`question${index + 1}`] = `Question ${index + 1} is required`;
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (isPythonMode) {
            if (!validatePythonForm()) {
                return;
            }
        } else {
            if (!validateLegacyForm()) {
                return;
            }

            if (isEdit && !questionId) {
                toast.error("Question ID is missing. Please try again.");
                return;
            }
        }

        setIsLoading(true);
        try {
            const websiteId = currentWebsiteId || Cookies.get("_website_id");

            if (!websiteId) {
                toast.error("Website ID is missing");
                setIsLoading(false);
                return;
            }

            let response;

            if (isPythonMode) {
                response = await PostData(
                    "ms1",
                    "api/v1/aiassistant/editquestion",
                    {
                        websiteId,
                        productId,
                        sku: formData.sku,
                        questions: pythonQuestions.map(q => q.trim()),
                    }
                );
            } else if (isEdit && questionId) {
                const questionIdStr = String(questionId);
                response = await PostData(
                    "ms1",
                    "api/v1/aiassistant/editquestion",
                    {
                        websiteId,
                        question: formData.question,
                        answer: formData.answer,
                        sku: formData.sku,
                        isActive: formData.isActive,
                        questionId: questionIdStr
                    }
                );
            } else {
                response = await PostData(
                    "ms1",
                    "api/v1/aiassistant/addquestion",
                    {
                        websiteId,
                        question: formData.question,
                        answer: formData.answer,
                        sku: formData.sku,
                        isActive: formData.isActive
                    }
                );
            }

            if (response !== "Invalid" && response !== undefined) {
                if (response.status === "success") {
                    localStorage.removeItem('editingProductQuestions');
                    localStorage.removeItem('editingQuestion');
                    toast.success(isPythonMode || isEdit ? 'Question updated successfully!' : 'Question added successfully!');
                    navigate('/aiassistant/questions');
                } else {
                    toast.error(response.message?.message || response.message || "Failed to save question");
                }
            } else {
                toast.error("Invalid response from server");
            }
        } catch (error) {
            console.error('Error saving question:', error);
            toast.error('Error saving question. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        localStorage.removeItem('editingProductQuestions');
        localStorage.removeItem('editingQuestion');
        setFormData({
            question: '',
            answer: '',
            sku: '',
            isActive: true
        });
        setPythonQuestions(['', '', '']);
        setErrors({});
        navigate('/aiassistant/questions');
    };

    if (isPythonMode) {
        return (
            <div className="question-form">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h2>Edit Product Questions</h2>
                    <Button
                        color="outline-secondary"
                        onClick={handleCancel}
                        className="d-flex align-items-center"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                        Back to Questions
                    </Button>
                </div>

                <div className="card">
                    <div className="card-body">
                        <div className="mb-4">
                            <h4 className="mb-2">Edit Product Questions</h4>
                            <p className="text-muted mb-0">
                                Update all 3 questions for this product. All questions are required.
                            </p>
                        </div>

                        <FormGroup className="mb-3">
                            <Label className="form-label fw-bold">Product ID</Label>
                            <Input type="text" value={productId} readOnly disabled />
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label className="form-label fw-bold">SKU</Label>
                            <Input type="text" value={formData.sku} readOnly disabled />
                        </FormGroup>

                        {[0, 1, 2].map((index) => (
                            <FormGroup className="mb-3" key={index}>
                                <Label className="form-label fw-bold">
                                    Question {index + 1} *
                                </Label>
                                <Input
                                    type="text"
                                    value={pythonQuestions[index]}
                                    onChange={(e) => handlePythonQuestionChange(index, e.target.value)}
                                    placeholder={`Enter question ${index + 1}`}
                                    className={errors[`question${index + 1}`] ? 'is-invalid' : ''}
                                />
                                {errors[`question${index + 1}`] && (
                                    <div className="invalid-feedback d-block">
                                        {errors[`question${index + 1}`]}
                                    </div>
                                )}
                            </FormGroup>
                        ))}

                        <div className="d-flex justify-content-end gap-2">
                            <Button color="secondary" outline onClick={handleCancel}>
                                Cancel
                            </Button>
                            <Button
                                color="primary"
                                onClick={handleSave}
                                disabled={isLoading}
                                className="d-flex align-items-center conversionBox"
                            >
                                <FontAwesomeIcon icon={faSave} className="me-2" />
                                {isLoading ? 'Saving...' : 'Update Questions'}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="question-form">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>{isEdit ? 'Edit Question' : 'Create Product Question'}</h2>
                <Button
                    color="outline-secondary"
                    onClick={handleCancel}
                    className="d-flex align-items-center"
                >
                    <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                    Back to Questions
                </Button>
            </div>

            <div className="card">
                <div className="card-body">
                    <div className="mb-4">
                        <h4 className="mb-2">
                            {isEdit ? 'Edit Question' : 'New Question'}
                        </h4>
                        <p className="text-muted mb-0">
                            {isEdit ? 'Update the question details below' : 'Add a new question for a product'}
                        </p>
                    </div>

                    <FormGroup className="mb-3">
                        <Label for="sku" className="form-label fw-bold">
                            Product SKU *
                        </Label>
                        <Input
                            type="text"
                            id="sku"
                            name="sku"
                            value={formData.sku}
                            onChange={handleInputChange}
                            placeholder="Enter the product SKU"
                            className={errors.sku ? 'is-invalid' : ''}
                        />
                        {errors.sku && (
                            <div className="invalid-feedback d-block">
                                {errors.sku}
                            </div>
                        )}
                    </FormGroup>

                    <FormGroup className="mb-3">
                        <Label for="question" className="form-label fw-bold">
                            Question *
                        </Label>
                        <Input
                            type="text"
                            id="question"
                            name="question"
                            value={formData.question}
                            onChange={handleInputChange}
                            placeholder="Enter the question"
                            className={errors.question ? 'is-invalid' : ''}
                        />
                        {errors.question && (
                            <div className="invalid-feedback d-block">
                                {errors.question}
                            </div>
                        )}
                    </FormGroup>

                    <FormGroup className="mb-3">
                        <Label for="answer" className="form-label fw-bold">
                            Answer
                        </Label>
                        <Input
                            type="textarea"
                            id="answer"
                            name="answer"
                            value={formData.answer}
                            onChange={handleInputChange}
                            placeholder="Enter the answer (optional)"
                            rows={4}
                            style={{ resize: 'vertical' }}
                        />
                    </FormGroup>

                    <FormGroup className="mb-4">
                        <div className="d-flex align-items-center gap-3">
                            <Label className="form-label fw-bold mb-0" style={{ lineHeight: '1', display: 'flex', alignItems: 'center', marginTop: '-7px' }}>
                                Status
                            </Label>
                            <div className="searchItemStatusWrp" style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                                <div className="form-check form-switch" style={{ margin: 0 }}>
                                    <Input
                                        className="form-check-input toggle-switch"
                                        type="checkbox"
                                        id="isActive"
                                        name="isActive"
                                        checked={formData.isActive}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>
                        </div>
                    </FormGroup>

                    <div className="d-flex justify-content-end gap-2">
                        <Button
                            color="secondary"
                            outline
                            onClick={handleCancel}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            onClick={handleSave}
                            disabled={isLoading}
                            className="d-flex align-items-center conversionBox"
                        >
                            <FontAwesomeIcon icon={faSave} className="me-2" />
                            {isLoading ? 'Saving...' : (isEdit ? 'Update Question' : 'Save Question')}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QuestionForm;
